import React, { useEffect, useState } from "react";
import { offmarketApi } from "../../lib/api";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { MoneyInput } from "../shared";

const FIELDS = [
  ["company", "Company", "text", "span2"], ["website", "Website", "text", "span2"],
  ["owner", "Owner / contact name", "text"], ["ownerTitle", "Title", "text"], ["email", "Owner email", "email"], ["phone", "Phone", "text"],
  ["linkedin", "LinkedIn URL", "text", "span2"], ["address", "Street address", "text", "span2"],
  ["city", "City", "text"], ["state", "State", "text"], ["revenue", "Revenue ($)", "money"], ["employees", "Employees", "number"],
  ["founded", "Founded (year)", "number"], ["type", "Type / segment", "text"], ["services", "Services / category", "text", "span2"],
  ["notes", "Notes", "text", "span4"],
];

// Manual edit of a target — fill in the owner when Apollo/web could not find one, fix any field.
export const EditTargetModal = ({ target, onClose, onSaved, fields = FIELDS, api = offmarketApi, title = "Edit target" }) => {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (target) setF(Object.fromEntries(fields.map(([k]) => [k, target[k] ?? ""]))); }, [target]); // eslint-disable-line
  if (!target) return null;

  const save = async () => {
    if (!String(f.company || "").trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      const patch = { ...f };
      fields.filter(([, , type]) => type === "number" || type === "money").forEach(([k]) => { patch[k] = patch[k] === "" || patch[k] == null ? null : Number(patch[k]); });
      const r = await api.update(target.id, patch);
      const who = f.owner ?? f.contact;
      if (who && who !== (target.owner ?? target.contact)) await api.activity(target.id, { kind: "note", note: `Contact entered manually: ${who}${f.email ? ` · ${f.email}` : ""}` }).catch(() => {});
      onSaved(r);
      toast.success(`${r.company} updated`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" data-testid="edit-target-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 760, width: "96%", maxHeight: "92vh", overflowY: "auto" }} onMouseDown={(e) => e.stopPropagation()}>
        <button className="modal-x" onClick={onClose} aria-label="Close" data-testid="edit-target-close"><X size={14} /></button>
        <div className="modal-title">{title}</div>
        <div className="modal-sub">Fix details or type in the contact yourself when Apollo and the web came up empty. Saving an email links this contact to the Outreach Inbox and campaigns.</div>
        <div className="om-crit-grid om-edit-grid">
          {fields.map(([k, label, type, span]) => (
            <div key={k} className={`field ${span || ""}`}>
              <label>{label}</label>
              {type === "money"
                ? <MoneyInput value={f[k] ?? ""} onChange={(v) => setF((s) => ({ ...s, [k]: v }))} testid={`edit-${k}`} />
                : <input className={`inp ${type === "number" ? "mono" : ""}`} type={type} value={f[k] ?? ""} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`edit-${k}`} />}
            </div>
          ))}
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose} data-testid="edit-target-cancel">Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="edit-target-save"><Save size={12} />{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
};
